import React, { useState, useEffect, useCallback, useRef } from 'react';
import './css/Carousel.css';

interface CarouselItem {
    id: number;
    type: 'image' | 'video';
    src: string;
    alt: string;
    description?: string;
    position?: string;
    fullWidth?: boolean;
}

interface CarouselProps {
    items: CarouselItem[];
    autoPlay?: boolean;
    autoPlayInterval?: number;
}

const Carousel: React.FC<CarouselProps> = ({
    items,
    autoPlay = true,
    autoPlayInterval = 6000
}) => {
    const [currentIndex, setCurrentIndex] = useState(0);
    const [isTransitioning, setIsTransitioning] = useState(false);
    const videoRefs = useRef<(HTMLVideoElement | null)[]>([]);
    const autoPlayIntervalRef = useRef<NodeJS.Timeout | null>(null);
    const [videoMuted, setVideoMuted] = useState(true); // Start muted for autoplay

    const nextSlide = useCallback(() => {
        if (!isTransitioning) {
            setIsTransitioning(true);

            // Pause current video if it exists
            const currentVideo = videoRefs.current[currentIndex];
            if (currentVideo && items[currentIndex].type === 'video') {
                currentVideo.pause();
            }

            setCurrentIndex(current => (current + 1) % items.length);
            setTimeout(() => setIsTransitioning(false), 600);
        }
    }, [items.length, isTransitioning, currentIndex, items]);

    const prevSlide = useCallback(() => {
        if (!isTransitioning) {
            setIsTransitioning(true);

            // Pause current video if it exists
            const currentVideo = videoRefs.current[currentIndex];
            if (currentVideo && items[currentIndex].type === 'video') {
                currentVideo.pause();
            }

            setCurrentIndex(current => (current - 1 + items.length) % items.length);
            setTimeout(() => setIsTransitioning(false), 600);
        }
    }, [items.length, isTransitioning, currentIndex, items]);

    const goToSlide = (index: number) => {
        if (!isTransitioning && index !== currentIndex) {
            setIsTransitioning(true);

            // Pause current video if it exists
            const currentVideo = videoRefs.current[currentIndex];
            if (currentVideo && items[currentIndex].type === 'video') {
                currentVideo.pause();
            }

            setCurrentIndex(index);
            setTimeout(() => setIsTransitioning(false), 600);
        }
    };

    const toggleMute = () => {
        setVideoMuted(!videoMuted);
        const currentVideo = videoRefs.current[currentIndex];
        if (currentVideo && items[currentIndex].type === 'video') {
            currentVideo.muted = !videoMuted;
        }
    };

    // Handle video play state when slides change
    useEffect(() => {
        // Reset auto-play interval
        if (autoPlayIntervalRef.current) {
            clearInterval(autoPlayIntervalRef.current);
        }

        if (autoPlay) {
            autoPlayIntervalRef.current = setInterval(nextSlide, autoPlayInterval);
        }

        // Handle video playback for current slide
        const currentVideo = videoRefs.current[currentIndex];
        const currentItem = items[currentIndex];

        if (currentVideo && currentItem.type === 'video') {
            // Set muted state
            currentVideo.muted = videoMuted;

            // Try to play the video immediately
            currentVideo.play().catch(error => {
                console.log("Video autoplay failed:", error);
            });
        }

        // Pause all other videos
        videoRefs.current.forEach((video, index) => {
            if (video && index !== currentIndex) {
                video.pause();
                video.currentTime = 0; // Reset to beginning
            }
        });

        return () => {
            if (autoPlayIntervalRef.current) {
                clearInterval(autoPlayIntervalRef.current);
            }
        };
    }, [currentIndex, autoPlay, autoPlayInterval, nextSlide, items, videoMuted]);

    return (
        <div className="carousel-container">
            <div className="carousel">
                <div
                    className="carousel-track"
                    style={{
                        transform: `translateX(-${currentIndex * 100}%)`
                    }}
                >
                    {items.map((item, index) => (
                        <div key={item.id} className={`carousel-slide ${item.fullWidth ? 'full-width' : ''}`}>
                            {item.type === 'video' ? (
                                <>
                                    <video
                                        ref={el => { videoRefs.current[index] = el }}
                                        className="carousel-video"
                                        src={item.src}
                                        autoPlay
                                        muted={videoMuted}
                                        loop
                                        playsInline
                                        preload="auto"
                                    />
                                    {/* Mute/unmute button */}
                                    <button
                                        onClick={toggleMute}
                                        style={{
                                            position: 'absolute',
                                            bottom: '20px',
                                            left: '20px',
                                            background: 'rgba(26, 47, 63, 0.8)',
                                            border: '2px solid #d4af37',
                                            borderRadius: '50%',
                                            width: '40px',
                                            height: '40px',
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            cursor: 'pointer',
                                            color: '#d4af37',
                                            fontSize: '20px',
                                            zIndex: 30
                                        }}
                                        aria-label={videoMuted ? "Unmute video" : "Mute video"}
                                    >
                                        {videoMuted ? '🔇' : '🔊'}
                                    </button>
                                </>
                            ) : (
                                <img
                                    src={item.src}
                                    alt={item.alt}
                                    className="carousel-image"
                                />
                            )}
                            {item.description && (
                                <div className={`carousel-overlay ${item.position || 'center'}`}>
                                    <p className="carousel-description">
                                        {item.description}
                                    </p>
                                </div>
                            )}
                        </div>
                    ))}
                </div>

                <button
                    className="carousel-button carousel-button-prev"
                    onClick={prevSlide}
                    aria-label="Previous slide"
                >
                    &#8249;
                </button>
                <button
                    className="carousel-button carousel-button-next"
                    onClick={nextSlide}
                    aria-label="Next slide"
                >
                    &#8250;
                </button>

                <div className="carousel-indicators">
                    {items.map((_, index) => (
                        <button
                            key={index}
                            className={`carousel-indicator ${index === currentIndex ? 'active' : ''}`}
                            onClick={() => goToSlide(index)}
                            aria-label={`Go to slide ${index + 1}`}
                        >
                            •
                        </button>
                    ))}
                </div>
            </div>
        </div>
    );
};

export default Carousel;